/**
 * Onboarding auto-tune dashboard (DD-NEXT-7) — query + mutation.
 *
 * GET  /api/v1/tenants/{tenant_id}/onboarding-autotune        → dashboard
 * PUT  /api/v1/tenants/{tenant_id}/onboarding-autotune/policy  → kill switch
 *
 * The path-tenant rewriter in api-client maps these to the
 * /api/tenant/v1 equivalents on a tenant host, so one pair of
 * hooks serves both platform-admin and tenant-admin contexts.
 *
 * Flipping the kill switch is an auth-surface mutation (it changes
 * the live default method users see), so the PUT is wrapped in
 * `withStepUp` — the backend route is behind requireStepUp +
 * tenant_settings.manage and will 401 the bare call otherwise.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { queryKeys } from "@/lib/query-keys";
import { autotuneDashboardSchema, type AutotuneDashboard } from "./schemas";

const base = (tenantID: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantID)}/onboarding-autotune`;

export async function fetchAutotuneDashboard(
  tenantID: string,
): Promise<AutotuneDashboard> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return autotuneDashboardSchema.parse(raw);
}

export function useAutotuneDashboardQuery(tenantID: string) {
  return useQuery<AutotuneDashboard, ApiError>({
    queryKey: queryKeys.onboardingAutotune(tenantID),
    queryFn: () => fetchAutotuneDashboard(tenantID),
    enabled: tenantID.length > 0,
    // The aggregator recomputes hourly; a 60s stale window keeps
    // the dashboard responsive to a manual refresh without
    // hammering the verdict table.
    staleTime: 60_000,
    retry: false,
  });
}

export function useSetAutotunePolicyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AutotuneDashboard, ApiError, boolean>({
    mutationFn: async (enabled) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(`${base(tenantID)}/policy`, {
          method: "PUT",
          body: { enabled },
        });
        // The PUT returns the fresh dashboard so the table
        // re-renders shadow→live in one round-trip.
        return autotuneDashboardSchema.parse(raw);
      }),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.onboardingAutotune(tenantID), fresh);
    },
  });
}
